import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Select,
  SelectItem,
  SelectSkeleton,
  Stack,
  TextArea,
  TextAreaSkeleton,
  TextInput,
  TextInputSkeleton,
  Tooltip
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import dayjs from 'dayjs';
import { create, eager, enforce, omitWhen, test } from 'vest';

import { useNotifications } from 'core/notifications';
import { useIsReadonly } from 'core/readonly';
import { routes } from 'core/routing';
import { useUser } from 'core/user/user';
import ProjectFormActions from 'features/inputs/components/ProjectFormActions';
import { useTrackProjectFormTabDirtyStatus } from 'features/inputs/components/ProjectFormNavigation';
import SharedWith from 'features/inputs/components/SharedWith';
import { useMeasurementSystem } from 'features/inputs/contexts/measurementSystemContext';
import { SITE_MEASUREMENT_SYSTEM, SITE_MEASUREMENT_SYSTEM_LABEL } from 'models/siteMeasurementSystem';
import { TIER, TIER_LABEL, TIER_TYPE } from 'models/tier';
import useCountriesQuery from 'shared/api/countries/useCountriesQuery';
import useMutateCreateProject from 'shared/api/projects/useMutateCreateProject';
import useMutateUpdateProjectDetails from 'shared/api/projects/useMutateUpdateProjectDetails';
import ShareProjectModal from 'shared/share-project/ShareProjectModal';
import { formatDate } from 'utils/dates';
import useForm from 'utils/hooks/useForm';
import useLatest from 'utils/hooks/useLatest';
import useUpdateEffect from 'utils/hooks/useUpdateEffect';
import { convertToCurrentSystem, convertToStandardSystem } from 'utils/units';
import styles from 'theme/features/inputs/project-details-form.module.scss';

const RENBY_LOSS_FACTOR = 0.27;

const suite = create((data = {}) => {
  eager();

  test('name', 'Project Name is required', () => {
    enforce(data.name).isNotEmpty();
    enforce(data.name).notMatches(/^[^-]+-[^-]+-\d{6}-$/);
  });

  test('description', 'Description is required', () => {
    enforce(data.description).isNotEmpty();
  });

  test('country', 'Country is required', () => {
    enforce(data.country).isNotEmpty();
  });

  test('city', 'City is required', () => {
    enforce(data.city).isNotEmpty();
  });

  omitWhen(data.siteMeasurementSystem === SITE_MEASUREMENT_SYSTEM.rebny.toString(), () => {
    test('lossFactor', 'Loss Factor is required', () => {
      enforce(data.lossFactor).notEquals(RENBY_LOSS_FACTOR.toString());
    });
  });
});

const TIERING = {
  ...TIER,
  auto: 0
};

const formatProjectName = ({ countryCode, city, projectName, createdDate }) =>
  `${countryCode}-${city}-${dayjs(createdDate).format('YYMMDD')}-${projectName || ''}`;

const parseProjectName = (name) => {
  const [, , , ...splits] = name.split('-');

  return splits.join('-') || name;
};

const mapFormValues = (values, measurementSystem) => ({
  name: parseProjectName(values.name),
  description: values.description,
  country: values.countryId.toString(),
  city: values.city,
  siteMeasurementSystem: values.siteMeasurementSystem.toString(),
  lossFactor: values.lossFactor?.toString() ?? '',
  circulationAmount: values.circulationAmount.toString(),
  estimatedArea: convertToCurrentSystem(values.estimatedArea, measurementSystem)?.toString() ?? '',
  address: values.address ?? '',
  state: values.state ?? '',
  zipCode: values.zipCode ?? '',
  notes: values.notes ?? '',
  tiering: values.tierType === TIER_TYPE.auto ? TIERING.auto : values.tier ?? ''
});

const ProjectDetailsForm = ({ values: projectDetails, onSaveChanges, isEditView = false, isLoading }) => {
  const navigate = useNavigate();

  const { user } = useUser();
  const isReadonly = useIsReadonly();

  const { showErrorNotification, showSuccessNotification } = useNotifications();

  const [isShareProjectModalOpen, setIsShareProjectModalOpen] = useState(false);

  const measurementSystemRef = useLatest(useMeasurementSystem());

  const { register, handleSubmit, watch, control, reset } = useForm({
    suite,
    defaultValues: projectDetails
      ? mapFormValues(projectDetails, measurementSystemRef.current)
      : {
          name: '',
          description: '',
          country: '',
          city: '',
          siteMeasurementSystem: SITE_MEASUREMENT_SYSTEM.boma.toString(),
          lossFactor: '0.2',
          circulationAmount: '0.3',
          estimatedArea: '',
          address: '',
          state: '',
          zipCode: '',
          notes: '',
          tiering: TIERING.auto
        }
  });

  const { isDirty: isFormDirty } = useTrackProjectFormTabDirtyStatus(control);

  useUpdateEffect(() => {
    if (projectDetails) {
      reset(mapFormValues(projectDetails, measurementSystemRef.current));
    }
  }, [projectDetails]);

  useUpdateEffect(() => {
    if (!isEditView && !isFormDirty && projectDetails) {
      navigate(routes.projectInputs(projectDetails.id), { replace: true, state: { refetchProject: false } });
    }
  }, [isFormDirty]);

  const selectedSiteMeasurementSystem = Number(watch('siteMeasurementSystem'));
  const selectedCountryId = Number(watch('country'));
  const selectedCity = watch('city');
  const selectedProjectName = watch('name');

  const { data: countries } = useCountriesQuery();
  const selectedCountry = useMemo(
    () => countries?.find((x) => x.id === selectedCountryId),
    [countries, selectedCountryId]
  );

  const createProjectMutation = useMutateCreateProject({
    onError: () => showErrorNotification({ description: 'Failed to create a project' }),
    onSuccess: (project) => {
      onSaveChanges(project);
      showSuccessNotification({ description: 'Project created successfully' });
    }
  });

  const updateProjectDetailsMutation = useMutateUpdateProjectDetails({
    onError: () => showErrorNotification({ description: 'Failed to update project details' }),
    onSuccess: (project) => {
      onSaveChanges(project);
      showSuccessNotification({ description: 'Project details updated successfully' });
    }
  });

  const handleFormSubmit = (values) => {
    const siteMeasurementSystem = Number(values.siteMeasurementSystem);
    const countryId = Number(values.country);

    const update = {
      ...projectDetails,
      address: values.address || null,
      state: values.state || null,
      zipCode: values.zipCode || null,
      notes: values.notes || null,
      name: formatProjectName({
        city: values.city,
        countryCode: selectedCountry.code,
        projectName: values.name,
        createdDate: projectDetails?.createdOn ?? new Date()
      }),
      description: values.description,
      city: values.city,
      countryId,
      measurementSystem: measurementSystemRef.current,
      siteMeasurementSystem,
      tier: Number(values.tiering) === TIERING.auto ? null : Number(values.tiering),
      tierType: Number(values.tiering) === TIERING.auto ? TIER_TYPE.auto : TIER_TYPE.custom,
      lossFactor:
        siteMeasurementSystem === SITE_MEASUREMENT_SYSTEM.rebny
          ? RENBY_LOSS_FACTOR
          : values.lossFactor
          ? Number(values.lossFactor)
          : null,
      circulationAmount: Number(values.circulationAmount),
      estimatedArea: values.estimatedArea
        ? convertToStandardSystem(Number(values.estimatedArea), measurementSystemRef.current)
        : null
    };

    if (isEditView) {
      updateProjectDetailsMutation.mutate({
        id: projectDetails.id,
        details: update
      });
    } else {
      createProjectMutation.mutate(update);
    }
  };

  const tieringLabel = (
    <span>
      Tier*
      <Tooltip
        align="top-left"
        className={styles['tier-tooltip']}
        label={
          <>
            This field allows you to override the automatic tier calculation performed by the tool. If you already know
            the tier for your project, please select it from the drop-down menu. <br /> <br />
            <strong>Tier 1 - Large Projects</strong> <br />
            Attending population >500 <br />
            Target density: 120 - 130 SF or 11 - 12 SQM per attending population <br /> <br />
            <strong>Tier 2 - Medium Projects</strong> <br />
            Attending population 50-500 <br />
            Target density: 100 - 110 SF or 9 - 10 SQM per attending population <br /> <br />
            <strong>Tier 3 - Limited Projects</strong> <br />
            Attending population {'<'}50 <br />
            Target density: 110 - 120 SF or 10 - 11 SQM per attending population
          </>
        }
      >
        <button className={styles['tooltip']} type="button">
          <Information />
        </button>
      </Tooltip>
    </span>
  );

  return (
    <>
      {projectDetails && (
        <ShareProjectModal
          projectId={projectDetails.id}
          onClose={() => setIsShareProjectModalOpen(false)}
          isOpen={isShareProjectModalOpen}
        />
      )}
      <Form className={styles.wrapper} onSubmit={handleSubmit(handleFormSubmit)} autoComplete="off">
        <div className={styles.form}>
          <Stack gap={7}>
            {isLoading ? (
              <TextInputSkeleton />
            ) : (
              <TextInput
                id="full-name"
                labelText={
                  <span>
                    Full Project Name
                    <Tooltip
                      align="top-left"
                      label="Enter country, city and project name to auto populate the full project name."
                    >
                      <button className={styles['tooltip']} type="button">
                        <Information />
                      </button>
                    </Tooltip>
                  </span>
                }
                value={
                  selectedCountry && selectedCity && selectedProjectName
                    ? formatProjectName({
                        city: selectedCity,
                        countryCode: selectedCountry.code,
                        projectName: selectedProjectName,
                        createdDate: projectDetails?.createdOn ?? new Date()
                      })
                    : 'Pending...'
                }
                readOnly
              />
            )}
            <div className={styles['input-group']}>
              {countries && !isLoading && !isReadonly ? (
                <Select id="country" labelText="Country*" {...register('country')}>
                  <SelectItem disabled hidden value="" text="Choose a country" />
                  {countries.map((c) => (
                    <SelectItem key={c.id} value={c.id} text={c.name} />
                  ))}
                </Select>
              ) : countries && !isLoading && isReadonly ? (
                <TextInput
                  id="country"
                  labelText="Country*"
                  value={countries?.find((c) => c.id === projectDetails.countryId)?.name ?? ''}
                  readOnly
                />
              ) : (
                <SelectSkeleton />
              )}
              {isLoading ? (
                <TextInputSkeleton />
              ) : (
                <TextInput
                  id="city"
                  labelText="City*"
                  placeholder="Enter a city"
                  autoComplete="new-password"
                  {...register('city')}
                  readOnly={isReadonly}
                />
              )}
            </div>
            <div className={styles['input-group']}>
              <div className={styles['input-group']}>
                {isLoading ? (
                  <TextInputSkeleton />
                ) : (
                  <TextInput
                    id="state"
                    labelText="State"
                    placeholder={isReadonly ? '-' : 'Enter a state'}
                    {...register('state')}
                    readOnly={isReadonly}
                  />
                )}
                {isLoading ? (
                  <TextInputSkeleton />
                ) : (
                  <TextInput
                    id="zip-code"
                    labelText="Zip/Postal Code"
                    placeholder={isReadonly ? '-' : 'Enter a zip code'}
                    {...register('zipCode')}
                    readOnly={isReadonly}
                  />
                )}
              </div>
              {isLoading ? (
                <TextInputSkeleton />
              ) : (
                <TextInput
                  id="street"
                  labelText="Street"
                  placeholder={isReadonly ? '-' : 'Enter a street'}
                  {...register('address')}
                  readOnly={isReadonly}
                />
              )}
            </div>
            {isLoading ? (
              <TextInputSkeleton />
            ) : (
              <TextInput
                id="project-name"
                labelText="Project Name*"
                placeholder="Enter a project name"
                {...register('name')}
                readOnly={isReadonly}
                autoComplete="new-password"
              />
            )}
            {isLoading ? (
              <TextAreaSkeleton />
            ) : (
              <TextArea
                id="description"
                labelText="Description*"
                placeholder="Add project-specific, real estate notes here"
                enableCounter
                maxCount={100}
                {...register('description')}
                readOnly={isReadonly}
              />
            )}
            <strong>Key Site Inputs</strong>
            <div className={styles['input-group']}>
              {isLoading ? (
                <SelectSkeleton />
              ) : isReadonly ? (
                <TextInput
                  id="tiering"
                  labelText={tieringLabel}
                  value={
                    projectDetails.tierType === TIER_TYPE.auto ? 'Automatic' : TIER_LABEL[projectDetails.tier] ?? 'N/A'
                  }
                  readOnly
                />
              ) : (
                <Select id="tiering" labelText={tieringLabel} {...register('tiering')}>
                  <SelectItem value={TIERING.auto} text="Automatic" />
                  {[TIERING.one, TIERING.two, TIERING.three].map((x) => (
                    <SelectItem key={x} value={x} text={TIER_LABEL[x]} />
                  ))}
                </Select>
              )}
              <div />
            </div>
            <div className={styles['input-group']}>
              {isLoading ? (
                <SelectSkeleton />
              ) : isReadonly ? (
                <TextInput
                  id="measurement-system"
                  labelText="Measurement System*"
                  value={SITE_MEASUREMENT_SYSTEM_LABEL[projectDetails.measurementSystem] ?? ''}
                  readOnly
                />
              ) : (
                <Select id="measurement-system" labelText="Measurement System*" {...register('siteMeasurementSystem')}>
                  {[SITE_MEASUREMENT_SYSTEM.boma, SITE_MEASUREMENT_SYSTEM.rebny].map((x) => (
                    <SelectItem key={x} value={x} text={SITE_MEASUREMENT_SYSTEM_LABEL[x]} />
                  ))}
                </Select>
              )}
              {isLoading ? (
                <SelectSkeleton />
              ) : selectedSiteMeasurementSystem === SITE_MEASUREMENT_SYSTEM.rebny ? (
                <TextInput
                  id="loss-factor"
                  labelText={
                    <span>
                      Loss Factor*
                      <Tooltip
                        align="top-left"
                        label="Loss factor is defined as the percentage difference between rentable area and usable area. It will vary across buildings and regions. Please work with your local Real Estate team and Broker to verify loss factor."
                      >
                        <button className={styles['tooltip']} type="button">
                          <Information />
                        </button>
                      </Tooltip>
                    </span>
                  }
                  value={`${RENBY_LOSS_FACTOR * 100}%`}
                  readOnly
                />
              ) : isReadonly ? (
                <TextInput
                  id="loss-factor"
                  labelText={
                    <span>
                      Loss Factor*
                      <Tooltip
                        align="top-left"
                        label="Loss factor is defined as the percentage difference between rentable area and usable area. It will vary across buildings and regions. Please work with your local Real Estate team and Broker to verify loss factor."
                      >
                        <button className={styles['tooltip']} type="button">
                          <Information />
                        </button>
                      </Tooltip>
                    </span>
                  }
                  value={`${projectDetails.lossFactor * 100}%`}
                  readOnly
                />
              ) : (
                <Select
                  id="loss-factor"
                  labelText={
                    <span>
                      Loss Factor*
                      <Tooltip
                        align="top-left"
                        label="Loss factor is defined as the percentage difference between rentable area and usable area. It will vary across buildings and regions. Please work with your local Real Estate team and Broker to verify loss factor."
                      >
                        <button className={styles['tooltip']} type="button">
                          <Information />
                        </button>
                      </Tooltip>
                    </span>
                  }
                  {...register('lossFactor')}
                >
                  <SelectItem value="" text="Unknown" />
                  <SelectItem value="0.1" text="10%" />
                  <SelectItem value="0.15" text="15%" />
                  <SelectItem value="0.2" text="20%" />
                  <SelectItem value="0.25" text="25%" />
                  <SelectItem value="0.3" text="30%" />
                  <SelectItem value="0.35" text="35%" />
                </Select>
              )}
            </div>
            <div className={styles['input-group']}>
              {isLoading ? (
                <SelectSkeleton />
              ) : isReadonly ? (
                <TextInput
                  id="circulation-amount"
                  labelText={
                    <span>
                      Circulation Amount*
                      <Tooltip
                        align="top-left"
                        label="This term refers to the primary and secondary circulation paths that exist between space types. Each space type captures tertiary circulation, such as the walking paths between desks."
                      >
                        <button className={styles['tooltip']} type="button">
                          <Information />
                        </button>
                      </Tooltip>
                    </span>
                  }
                  value={`${projectDetails.circulationAmount * 100}%`}
                  readOnly
                />
              ) : (
                <Select
                  id="circulation-amount"
                  {...register('circulationAmount')}
                  labelText={
                    <span>
                      Circulation Amount*
                      <Tooltip
                        align="top-left"
                        label="This term refers to the primary and secondary circulation paths that exist between space types. Each space type captures tertiary circulation, such as the walking paths between desks."
                      >
                        <button className={styles['tooltip']} type="button">
                          <Information />
                        </button>
                      </Tooltip>
                    </span>
                  }
                >
                  <SelectItem value="0.2" text="20%" />
                  <SelectItem value="0.25" text="25%" />
                  <SelectItem value="0.3" text="30%" />
                  <SelectItem value="0.35" text="35%" />
                  <SelectItem value="0.4" text="40%" />
                  <SelectItem value="0.45" text="45%" />
                  <SelectItem value="0.5" text="50%" />
                  <SelectItem value="0.55" text="55%" />
                  <SelectItem value="0.6" text="60%" />
                </Select>
              )}
              {isLoading ? (
                <TextInputSkeleton />
              ) : (
                <TextInput
                  id="area"
                  labelText="Available Area"
                  placeholder={isReadonly ? '-' : 'Enter a number'}
                  type="number"
                  min={0}
                  {...register('estimatedArea')}
                  readOnly={isReadonly}
                />
              )}
            </div>
            <div className={styles['input-group']}>
              {isLoading ? (
                <>
                  <TextInputSkeleton />
                  <TextInputSkeleton />
                </>
              ) : (
                <>
                  <TextInput
                    id="created-by"
                    labelText="Created By"
                    value={projectDetails?.userName ?? user.name}
                    readOnly
                  />
                  <TextInput
                    id="created-date"
                    labelText="Created Date"
                    value={formatDate(projectDetails?.createdOn ?? new Date())}
                    readOnly
                  />
                </>
              )}
            </div>
            {isLoading ? (
              <TextAreaSkeleton />
            ) : (
              <TextArea
                id="notes"
                labelText="Additional Notes"
                placeholder={isReadonly ? 'No notes' : 'Type here'}
                enableCounter
                maxCount={100}
                {...register('notes')}
                readOnly={isReadonly}
              />
            )}
          </Stack>
          <ProjectFormActions
            projectId={projectDetails?.id}
            kind="secondary"
            primaryText={isEditView ? 'Save Changes' : 'Create Project'}
            secondaryText={isEditView && !isReadonly ? 'Share Project' : null}
            isPrimaryLoading={createProjectMutation.isLoading || updateProjectDetailsMutation.isLoading}
            isLoading={isLoading}
            onSecondaryClick={() => setIsShareProjectModalOpen(true)}
          />
        </div>
        <SharedWith projectId={projectDetails?.id} />
      </Form>
    </>
  );
};

export default ProjectDetailsForm;
